/**
 * Custom Sticky Add to Cart
 */

import { getScrollTop, getScrollContainer } from '@theme/scroll-container';
import { formatMoney } from '@theme/money-formatting';

class StickyCartEnhancer {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.enhanceExistingSections());
    } else {
      this.enhanceExistingSections();
    }

    this.observeForNewSections();
  }

  enhanceExistingSections() {
    const stickyCartSections = document.querySelectorAll('[data-sticky-cart]');
    
    stickyCartSections.forEach(section => {
      if (!this.instances.has(section)) {
        this.enhanceSection(section);
      }
    });
  }

  enhanceSection(container) {
    const instance = new StickyCartInstance(container);
    this.instances.set(container, instance);
    
    container.setAttribute('data-enhanced', 'true');
  }

  observeForNewSections() {
    const observer = new MutationObserver((mutations) => {
      let hasNewSections = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches('[data-sticky-cart]:not([data-enhanced])')) {
              hasNewSections = true;
            } else if (node.querySelector) {
              const newSection = node.querySelector('[data-sticky-cart]:not([data-enhanced])');
              if (newSection) {
                hasNewSections = true;
              }
            }
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1 && this.instances.has(node)) {
            const instance = this.instances.get(node);
            instance.cleanup();
            this.instances.delete(node);
          }
        });
      });
      
      if (hasNewSections) {
        setTimeout(() => this.enhanceExistingSections(), 10);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

/**
 * Unten angedockte Overlays (Omnisend-Formulare/Teaser, eigenes Formular-Pop-up in der Ecke) über die Sticky-Leiste schieben,
 * solange sie unten sichtbar ist. Vollbild-Popups bleiben unverändert.
 */
const overlayLift = {
  // Omnisend-Formulare/Teaser und das eigene Formular als Pop-up in der Ecke
  selectors: ['#omnisend-forms-wrapper', 'custom-form.cf--corner'],
  gap: 8,
  bar: null,
  observer: null,
  watched: new WeakSet(),
  frame: 0,
  bound: false,

  /** bar = sichtbare Leiste unten, oder null */
  set(bar) {
    if (bar === this.bar) return;
    this.bar = bar;
    this.watch();
    this.schedule();
  },

  watch() {
    if (!this.observer) {
      this.observer = new MutationObserver(() => this.schedule());
      // Neue App-Container (Formulare werden erst beim Anzeigen eingefügt)
      this.observer.observe(document.body, { childList: true });
    }
    if (!this.bound) {
      this.bound = true;
      // Nach Ein-/Ausblend-Animationen und bei Größenänderung (z. B. Safari-Leiste) neu messen
      const later = (event) => {
        if (!event || this.roots().some((root) => root.contains(event.target)) || this.bar?.contains(event.target)) this.schedule();
      };
      document.addEventListener('transitionend', later, true);
      document.addEventListener('animationend', later, true);
      window.addEventListener('resize', () => this.schedule(), { passive: true });
    }
    // Ein-/Ausblenden passiert über style/class – nur innerhalb der App-Container beobachten
    this.roots().forEach((root) => {
      if (this.watched.has(root)) return;
      this.watched.add(root);
      this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    });
  },

  roots() {
    return this.selectors.flatMap((selector) => [...document.querySelectorAll(selector)]);
  },

  schedule() {
    clearTimeout(this.frame);
    this.frame = setTimeout(() => this.apply(), 30);
  },

  /** Oberkante der Leiste in ihrer Endposition (auch während sie noch hineinfährt), inkl. überstehender Inhalte */
  barTop() {
    const bar = this.bar;
    const shift = new DOMMatrixReadOnly(getComputedStyle(bar).transform).m42 || 0;
    const box = bar.getBoundingClientRect();
    let top = box.top;
    // Inhalte, die oben aus der Leiste herausragen (z. B. Text über dem Knopf) – keine eigenständig fixierten Elemente
    bar.querySelectorAll('*').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!r.height || !r.width || r.top < box.top - 150 || r.bottom > box.bottom + 1) return;
      if (getComputedStyle(el).position === 'fixed') return;
      top = Math.min(top, r.top);
    });
    return top - shift;
  },

  /** Unterkante des sichtbaren Inhalts ohne unsere Verschiebung (Rahmen können 0 px hoch sein) */
  naturalBottom(el) {
    // Aktuelle (ggf. gerade animierte) Verschiebung herausrechnen – nicht den Zielwert, sonst schaukelt es sich auf
    const computed = getComputedStyle(el).translate;
    const current = computed && computed !== 'none' ? parseFloat(computed.split(' ')[1]) || 0 : 0;
    let bottom = -Infinity;
    [el, ...el.querySelectorAll('*')].forEach((node) => {
      const r = node.getBoundingClientRect();
      if (r.height > 0 && r.width > 0) bottom = Math.max(bottom, r.bottom);
    });
    return bottom === -Infinity ? null : bottom - current;
  },

  lift(el, barTop) {
    let value = '';
    if (barTop !== null) {
      const bottom = this.naturalBottom(el);
      const shift = bottom === null ? 0 : Math.ceil(bottom - barTop + this.gap);
      if (shift > 0) value = `0 -${shift}px`;
    }
    if (el.style.getPropertyValue('translate') !== value) el.style.setProperty('translate', value);
    if (el.style.getPropertyValue('transition') !== 'translate 0.3s ease') el.style.setProperty('transition', 'translate 0.3s ease');
  },

  apply() {
    this.watch();
    const viewport = window.innerHeight;
    const barTop = this.bar ? this.barTop() : null;
    this.roots().forEach((root) => {
      [root, ...root.querySelectorAll('*')].forEach((el) => {
        if (el.hasAttribute('data-sticky-lift') || el.parentElement?.closest('[data-sticky-lift]')) return;
        const style = getComputedStyle(el);
        if (style.position !== 'fixed' || style.display === 'none') return;
        // Nicht dargestellt (z. B. in einem ausgeblendeten Popup) → später erneut prüfen
        if (!el.getClientRects().length) return;
        const rect = el.getBoundingClientRect();
        // Unten angedockt: Unterkante am Bildschirmrand (Abstand < 200 px)
        if (viewport - rect.bottom >= 200) return;
        if (rect.height <= viewport * 0.8) {
          // Unten angedockt (Teaser, Leiste) – auch ein 0 px hoher Rahmen mit absolut positioniertem Inhalt
          el.setAttribute('data-sticky-lift', '');
          return;
        }
        // Vollbild-Ebene: nur einen Inhalt markieren, der unten am Rand klebt (z. B. Flyout von unten)
        const content = [...el.querySelectorAll('*')].find((child) => {
          const r = child.getBoundingClientRect();
          return r.height > 0 && r.height <= viewport * 0.8 && r.bottom >= viewport - 4;
        });
        if (content) content.setAttribute('data-sticky-lift', '');
      });
      [root, ...root.querySelectorAll('*')].filter((el) => el.hasAttribute('data-sticky-lift')).forEach((el) => this.lift(el, barTop));
    });
  },
};

class StickyCartInstance {
  constructor(container) {
    this.container = container;
    this.abortController = new AbortController();
    this.variantChangeTimeout = null;
    this.currentVariantId = null;
    this.variants = [];
    this.footerElement = null;
    
    this.init();
  }

  init() {
    this.calculateHeaderHeight();
    this.cacheFooterElement();
    
    try {
      this.variants = JSON.parse(this.container.dataset.variants || '[]');
    } catch (e) {
      console.error('Error parsing variants:', e);
      return;
    }

    const { signal } = this.abortController;
    
    // Horizon scrollt am Desktop (≥990px) in .page-wrapper statt im Fenster. Scroll-Events
    // bubbeln nicht, darum in der Capture-Phase am document lauschen (fängt beide Fälle).
    document.addEventListener('scroll', this.handleScroll, { signal, passive: true, capture: true });
    window.addEventListener('resize', this.calculateHeaderHeight, { signal, passive: true });
    
    document.addEventListener('variant:update', this.handleProductPageVariantUpdate, { signal });
    document.addEventListener('variant:selected', this.handleVariantSelected, { signal });
    
    this.setupVariantSelects();
    this.setupAddToCartButton();
    this.initializeCurrentVariant();
    this.handleScroll();
  }

  cleanup() {
    this.abortController.abort();
    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
    }
  }

  cacheFooterElement() {
    this.footerElement = document.querySelector('footer, .footer, #footer, [role="contentinfo"]');
  }

  calculateHeaderHeight = () => {
    const stickySection = document.querySelector('.sticky-add-to-cart-section.sticky-position-top');
    if (!stickySection) return;
    
    const header = document.querySelector('#header-component, header, .header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  };

  handleScroll = () => {
    const shouldShow = this.checkShowTrigger();
    const shouldHide = this.checkHideTrigger();
    
    const isVisible = shouldShow && !shouldHide;
    this.container.classList.toggle('is-visible', isVisible);

    // Leiste unten sichtbar: Omnisend & Co. um ihre Höhe nach oben schieben
    if (this.container.dataset.position !== 'top') {
      if (isVisible) overlayLift.set(this.container);
      else if (overlayLift.bar === this.container) overlayLift.set(null);
    }
  };

  checkShowTrigger() {
    // Warenkorb-Knopf des Produktformulars, nicht der Knopf einer Sticky-Leiste
    const triggerButton = [...document.querySelectorAll('.button.add-to-cart-button')].find(
      (button) => !button.closest('[data-sticky-cart], .sticky-add-to-cart__bar')
    );
    const mode = this.container.dataset.scrollTrigger;
    const pixelOffset = parseInt(this.container.dataset.scrollOffset, 10) || 0;

    if (mode === 'always') {
      return true;
    } else if (mode === 'button') {
      // Kein Warenkorb-Knopf auf der Seite (z.B. Listicle) → immer anzeigen
      if (!triggerButton) return true;
      // Knopf ist nach oben aus dem sichtbaren Bereich gescrollt
      return triggerButton.getBoundingClientRect().bottom < 0;
    } else if (mode === 'scroll_trigger') {
      return getScrollTop() > pixelOffset;
    }

    return false;
  }

  checkHideTrigger() {
    const hideMode = this.container.dataset.scrollHideTrigger;
    
    switch (hideMode) {
      case 'footer':
        if (!this.footerElement) return false;
        const footerRect = this.footerElement.getBoundingClientRect();
        return footerRect.top <= window.innerHeight;
        
      case 'bottom':
        const scroller = getScrollContainer();
        return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 10;
        
      case 'pixel_offset':
        const hideOffset = parseInt(this.container.dataset.scrollHideOffset, 10) || 300;
        const scrollBox = getScrollContainer();
        const distanceFromBottom = scrollBox.scrollHeight - (scrollBox.scrollTop + scrollBox.clientHeight);
        return distanceFromBottom <= hideOffset;
        
      case 'custom_element':
        const elementId = this.container.dataset.hideElementId;
        if (!elementId) return false;
        
        const customElement = document.getElementById(elementId);
        if (!customElement) return false;
        
        const elementRect = customElement.getBoundingClientRect();
        return elementRect.top <= window.innerHeight;
        
      case 'never':
      default:
        return false;
    }
  }

  setupVariantSelects() {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    
    variantSelects.forEach(select => {
      select.addEventListener('change', () => {
        clearTimeout(this.variantChangeTimeout);
        this.variantChangeTimeout = setTimeout(() => {
          this.handleVariantChange(false);
        }, 100);
      }, { signal: this.abortController.signal });
    });
  }

  setupAddToCartButton() {
    if (this.container.dataset.buttonMode === 'link') return;

    const button = this.container.querySelector('.sticky-add-to-cart');
    if (button) {
      button.addEventListener('click', this.addToCart, {
        signal: this.abortController.signal
      });
    }
  }

  handleVariantChange(fromProductPage = false) {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    const selectedValues = Array.from(variantSelects).map(select => select.value);
    
    const matchingVariant = this.variants.find(variant => {
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return selectedValues.every((value, index) => variantOptions[index] === value);
    });
    
    if (matchingVariant) {
      this.updateVariantDisplay(matchingVariant);
      
      if (!fromProductPage && this.currentVariantId !== matchingVariant.id) {
        this.currentVariantId = matchingVariant.id;
        this.dispatchStickyVariantEvent(matchingVariant);
      }
    }
  }

  handleProductPageVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.container.dataset.productId) return;
    
    const variant = event.detail.resource;
    if (!variant || !variant.id) return;
    
    if (this.currentVariantId === String(variant.id)) return;
    
    this.currentVariantId = String(variant.id);
    
    const matchingVariant = this.variants.find(v => String(v.id) === String(variant.id));
    if (!matchingVariant) return;
    
    this.updateSelectsFromVariant(matchingVariant);
    this.updateVariantDisplay(matchingVariant);
  };

  handleVariantSelected = (event) => {
    if (event.target === this.container) return;
    
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    
    const matchingVariant = this.variants.find(v => String(v.id) === String(variantId));
    if (matchingVariant && this.currentVariantId !== String(variantId)) {
      this.currentVariantId = String(variantId);
      this.updateSelectsFromVariant(matchingVariant);
      this.updateVariantDisplay(matchingVariant);
    }
  };

  dispatchStickyVariantEvent(variant) {
    const variantSelectedEvent = new CustomEvent('variant:selected', {
      bubbles: true,
      detail: {
        resource: { id: String(variant.id) }
      }
    });
    
    document.dispatchEvent(variantSelectedEvent);
    
    if (window.Theme?.template?.name === 'product') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }
  }

  updateSelectsFromVariant(variant) {
    const variantSelects = this.container.querySelectorAll('.sticky-variant-select');
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    
    variantSelects.forEach((select, index) => {
      if (options[index]) {
        select.value = options[index];
      }
    });
  }

  updateVariantDisplay(variant) {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    button.dataset.productId = variant.id;
    
    const priceWrapper = this.container.querySelector('.sticky-product-price');
    if (priceWrapper) {
      this.updatePriceDisplay(priceWrapper, variant);
    }
    
    const productImage = this.container.querySelector('.sticky-product-image');
    // Eigenes Bild aus den Section-Einstellungen nicht durch das Variantenbild ersetzen
    if (variant.featured_image && productImage && !productImage.hasAttribute('data-custom-image')) {
      const newImageUrl = variant.featured_image;
      if (newImageUrl && productImage.src !== newImageUrl) {
        productImage.src = newImageUrl;
      }
    }
    
    this.updateButtonState(variant);
  }

  updatePriceDisplay(priceWrapper, variant) {
    const regularPrice = priceWrapper.querySelector('.sticky-price-regular');
    const salePrice = priceWrapper.querySelector('.sticky-price-sale');
    const comparePrice = priceWrapper.querySelector('.sticky-price-compare');
    
    // Preisformat aus den Shopify-Einstellungen (z. B. „{{amount_with_comma_separator}} €“)
    const moneyFormat = this.container.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    const currency = this.container.dataset.currency || 'EUR';
    const formatPrice = (cents) => formatMoney(cents, moneyFormat, currency);
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (salePrice) {
        salePrice.textContent = formatPrice(variant.price);
      } else if (regularPrice) {
        regularPrice.textContent = formatPrice(variant.price);
      }
      
      if (comparePrice) {
        comparePrice.textContent = formatPrice(variant.compare_at_price);
        comparePrice.style.display = '';
      }
    } else {
      if (regularPrice) {
        regularPrice.textContent = formatPrice(variant.price);
      }
      if (comparePrice) {
        comparePrice.style.display = 'none';
      }
    }
  }

  updateButtonState(variant) {
    if (this.container.dataset.buttonMode === 'link') return;

    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    const buttonText = button.querySelector('.sticky-add-to-cart-text');
    const originalButtonText = button.dataset.originalText || 'Add to Cart';
    const productTitle = this.container.dataset.productTitle || '';
    
    if (variant.available) {
      button.disabled = false;
      if (buttonText) buttonText.innerHTML = originalButtonText; // kann Formatierung enthalten
      button.setAttribute('aria-label', `Add ${productTitle} to cart`);
    } else {
      button.disabled = true;
      if (buttonText) buttonText.textContent = 'Ausverkauft';
      button.setAttribute('aria-label', `${productTitle} is sold out`);
    }
  }

  initializeCurrentVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    
    if (urlVariantId) {
      const urlVariant = this.variants.find(v => String(v.id) === String(urlVariantId));
      if (urlVariant) {
        this.currentVariantId = String(urlVariantId);
        this.updateSelectsFromVariant(urlVariant);
        this.updateVariantDisplay(urlVariant);
        return;
      }
    }
    
    this.handleVariantChange(true);
  }

  addToCart = () => {
    const button = this.container.querySelector('.sticky-add-to-cart');
    if (!button) return;
    
    const variantId = button.dataset.productId;
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }
    
    const selectedVariant = this.variants.find(v => String(v.id) === String(variantId));
    if (!selectedVariant) {
      console.error('Variant not found:', variantId);
      this.showOverlay('Selected variant not found. Please try again.');
      return;
    }
    
    const buttonText = button.querySelector('.sticky-add-to-cart-text');
    const originalText = buttonText ? buttonText.innerHTML : 'In den Warenkorb';
    
    button.disabled = true;
    if (buttonText) buttonText.textContent = 'Adding...';
    
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    
    const sectionsToUpdate = this.getSectionsToUpdate();
    if (sectionsToUpdate.length > 0) {
      formData.append('sections', sectionsToUpdate.join(','));
    }
    
    this.triggerFlyToCartAnimation();
    
    fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json, text/javascript'
      }
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorData => {
          console.error('Shopify cart error:', errorData);
          this.showOverlay(errorData.message || 'Failed to add to cart');
          throw new Error(errorData.message || 'Failed to add to cart');
        });
      }
      return response.json();
    })
    .then(response => {
      if (buttonText) buttonText.textContent = 'Added!';
      
      // Horizon 4.x: Einschub, Warenkorbseite und Warenkorb-Symbol rendern sich über das
      // Standard-Event selbst neu (kein manuelles Ersetzen der Sections mehr).
      this.dispatchCartEvents(response, response.sections || {}, variantId);
      this.updateCartAndOpenDrawer();
      
      setTimeout(() => {
        button.disabled = false;
        if (buttonText) buttonText.innerHTML = originalText;
      }, 2000);
    })
    .catch(error => {
      console.error('Cart error:', error);
      
      button.disabled = false;
      if (buttonText) buttonText.innerHTML = originalText;
      
      const errorEvent = new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          sourceId: this.container.dataset.sectionId || 'sticky-cart',
          data: {
            message: error.message || 'Failed to add item to cart',
            originalError: error
          }
        }
      });
      
      document.dispatchEvent(errorEvent);
    });
  };

  getSectionsToUpdate() {
    const sections = [];
    
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach(component => {
      if (component.dataset.sectionId) {
        sections.push(component.dataset.sectionId);
      }
    });
    
    const cartIconSection = document.querySelector('.header-actions__cart-icon')?.closest('[id^="shopify-section-"]');
    if (cartIconSection) {
      const sectionId = cartIconSection.id.replace('shopify-section-', '');
      if (sectionId) sections.push(sectionId);
    }
    
    return sections;
  }

  showOverlay(message, duration = 3000) {
    let overlay = document.getElementById('messageOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'messageOverlay';
      document.body.appendChild(overlay);
    }
    overlay.textContent = message;
    overlay.classList.add('show');
    setTimeout(() => overlay.classList.remove('show'), duration);
  }

  triggerFlyToCartAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const button = this.container.querySelector('.sticky-add-to-cart');
    const productImage = this.container.querySelector('.sticky-product-image');
    
    if (!cartIcon || !button || !productImage) return;
    
    const flyToCartElement = document.createElement('fly-to-cart');
    const imageUrl = productImage.src || productImage.dataset.src;
    
    if (imageUrl) {
      flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
    }
    
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  async dispatchCartEvents(response, sections, variantId) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();

      this.container.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      const ajaxCart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          source: 'sticky-cart-component',
          sourceId: this.container.dataset.sectionId || 'sticky-cart',
          productId: this.container.dataset.productId,
          variantId: response.variant_id,
          items: ajaxCart.items,
          itemCount: 1,
          sections: sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[sticky-cart] Warenkorb-Event fehlgeschlagen:', error);
    }
  }

  updateCartAndOpenDrawer() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        this.updateCartIconCount(cart.item_count);
        
        setTimeout(() => {
          this.openCartDrawer();
        }, 600);
      })
      .catch(error => {
        console.error('Error fetching updated cart:', error);
      });
  }

  updateCartIconCount(itemCount) {
    const cartCountElements = document.querySelectorAll('.cart-bubble__text-count, .cart-count-bubble, [data-cart-count]');
    
    cartCountElements.forEach(element => {
      if (element) {
        element.textContent = itemCount;
        
        const bubble = element.closest('.cart-bubble');
        if (bubble) {
          bubble.classList.toggle('visually-hidden', itemCount === 0);
        }
      }
    });
    
    const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    cartIcons.forEach(icon => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
    
    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now()
    }));
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component, cart-drawer, [data-cart-drawer]');
    
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
      } else {
        const cartTrigger = document.querySelector('[data-cart-trigger], .header-actions__cart');
        cartTrigger?.click();
      }
    }
  }
}

new StickyCartEnhancer();